/**
 * Portal Atendimento — TanStack Query hooks (PRD 4, Story 4.1.5).
 *
 * Espelho do ``useAtendimentos.ts`` (painel interno) para o lado do
 * Portal Parceiro. Namespace de KEYS é distinto (``portal-atendimentos``
 * vs ``atendimentos``) pra evitar colisão de cache se as duas telas
 * forem montadas na mesma sessão de testes.
 *
 * Polling: 15s pra lista, 5s pra mensagens (mesmo trade-off do interno).
 * WebSocket virá em Story 4.6.x.
 *
 * Pattern reference: V1/src/hooks/useAtendimentos.ts
 */

import { useMemo, useRef } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";

import {
  portalAtendimentosApi,
  type PortalSendMessageBody,
  type PortalStartSessionRequest,
} from "@/services/portalAtendimentosApi";
import type { CustomerSessionMessage, MessageListResponse } from "@/types/atendimento";

const KEYS = {
  list: () => ["portal-atendimentos", "list"] as const,
  messages: (id: string) => ["portal-atendimentos", "messages", id] as const,
  sectors: () => ["portal-atendimentos", "sectors"] as const,
  voiceConsent: () => ["portal-atendimentos", "voice-consent"] as const,
  chamada: (id: string) => ["portal-atendimentos", "chamada", id] as const,
};

/**
 * D3 (passo 19): a resposta do atendente chegou pelo socket. Refaz a LISTA de sessões (status, última mensagem) e, com `sessionId`, o
 * POLL de mensagens dessa sessão (o `after_id` traz só as novas). Nunca a carga infinita (`KEYS.messages`): refazê-la abriria um buraco
 * com as páginas antigas. ⚠️ O `invalidateQueries` refaz consulta ativa mesmo com a aba oculta, e o servidor marca `read_at` em toda busca:
 * por isso o poll leva `paramsDeLeitura()` (`marcar_lidas=false` com a aba escondida).
 */
export function invalidarAtendimentoDoPortal(qc: QueryClient, sessionId?: string | null): void {
  void qc.invalidateQueries({ queryKey: KEYS.list() });
  if (sessionId) {
    void qc.invalidateQueries({ queryKey: [...KEYS.messages(sessionId), "poll"] });
  }
}

/**
 * PR 1, Decisão 10: o servidor grava `read_at` nas respostas do atendente em TODA busca de mensagens da sessão (a carga, "carregar mais
 * antigas" e o poll de 5 s). Com a aba escondida a busca vai com `marcar_lidas=false`: uma conversa aberta numa aba esquecida não marca
 * como lida a resposta que o parceiro não viu (o push e o e-mail de 15 min pulam resposta lida). Visível, NADA é mandado — o padrão do
 * servidor é `true`, como sempre foi, e o front novo continua falando com o servidor antigo. Ao voltar a aba, o `PortalAoVivo` refaz o poll
 * sem o parâmetro: é essa busca que "abre" a conversa (o servidor confirma por `atendimento:lidas`).
 */
export function paramsDeLeitura(): { marcar_lidas?: false } {
  return typeof document !== "undefined" && document.visibilityState !== "visible"
    ? { marcar_lidas: false }
    : {};
}

/** Mensagens por página (carga inicial + cada "carregar mais" ao rolar pra cima). */
const MSG_PAGE_SIZE = 30;

/** Append imutável de mensagens novas na página 0 (as mais recentes) do cache
 *  infinito, deduplicando por id. (mesmo padrão do painel interno). */
function appendToNewestPage(
  old: InfiniteData<MessageListResponse> | undefined,
  fresh: CustomerSessionMessage[]
): InfiniteData<MessageListResponse> | undefined {
  if (!old || !old.pages.length || !fresh.length) return old;
  const seen = new Set(old.pages.flatMap((p) => p.items.map((m) => m.id)));
  const toAdd = fresh.filter((m) => !seen.has(m.id));
  if (!toAdd.length) return old;
  const pages = [...old.pages];
  pages[0] = { ...pages[0], items: [...pages[0].items, ...toAdd] };
  return { ...old, pages };
}

/**
 * Chamada em andamento na sessão — poll 5s (07/08/2026).
 *
 * 5s e não 15s porque isto é uma CHAMADA: alguém está do outro lado esperando.
 * Meio minuto de atraso para a faixa aparecer é a diferença entre atender e o
 * atendente desistir. O endpoint é barato justamente para suportar esse ritmo.
 */
export const useChamadaEmAndamento = (sessionId: string | null) =>
  useQuery({
    queryKey: KEYS.chamada(sessionId ?? "_noop"),
    queryFn: () => portalAtendimentosApi.getChamada(sessionId as string),
    enabled: sessionId !== null,
    refetchInterval: 5_000,
  });

/** Lista paginada das sessões do parceiro autenticado (poll 15s). */
export const usePortalSessions = () =>
  useQuery({
    queryKey: KEYS.list(),
    queryFn: () => portalAtendimentosApi.listMySessions(),
    refetchInterval: 15_000,
  });

/**
 * Histórico de mensagens da sessão — CURSOR + lazy-load (Ciclo 2, espelha o painel
 * interno). Carrega as 30 mais recentes; `loadOlder` busca as anteriores por cursor
 * (scroll pra cima); poll 5s por `after_id` faz APPEND das novas (sem re-baixar o
 * histórico — crítico pro portal, cujas imagens são base64 inline pesado). Retorna
 * lista ASCENDENTE achatada + controles de paginação.
 */
export const usePortalMessages = (sessionId: string | null) => {
  const qc = useQueryClient();
  const key = sessionId
    ? KEYS.messages(sessionId)
    : (["portal-atendimentos", "messages", "_noop"] as const);

  const infinite = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) =>
      portalAtendimentosApi.listMessages(sessionId as string, {
        limit: MSG_PAGE_SIZE,
        before_id: pageParam ?? undefined,
        ...paramsDeLeitura(),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.has_more && lastPage.items.length ? lastPage.items[0].id : undefined,
    enabled: sessionId !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const messages = useMemo(
    () => (infinite.data ? [...infinite.data.pages].reverse().flatMap((p) => p.items) : []),
    [infinite.data]
  );

  const newestIdRef = useRef<string | null>(null);
  newestIdRef.current = messages.length ? messages[messages.length - 1].id : null;

  useQuery({
    queryKey: [...key, "poll"],
    queryFn: async () => {
      if (!sessionId) return [] as CustomerSessionMessage[];
      const after = newestIdRef.current;
      const res = await portalAtendimentosApi.listMessages(
        sessionId,
        after
          ? { limit: 50, after_id: after, ...paramsDeLeitura() }
          : { limit: MSG_PAGE_SIZE, ...paramsDeLeitura() }
      );
      if (res.items.length) {
        qc.setQueryData<InfiniteData<MessageListResponse>>(key, (old) =>
          appendToNewestPage(old, res.items)
        );
      }
      return res.items;
    },
    enabled: sessionId !== null && infinite.isSuccess,
    refetchInterval: 5_000,
    refetchOnWindowFocus: false,
  });

  return {
    messages,
    isLoading: infinite.isLoading,
    isError: infinite.isError,
    hasMoreOlder: infinite.hasNextPage,
    loadOlder: infinite.fetchNextPage,
    isLoadingOlder: infinite.isFetchingNextPage,
  };
};

/**
 * Lista de setores ativos do tenant para o dropdown de "Abrir nova sessão".
 *
 * Sem polling — setores raramente mudam e o portal monta a lista uma
 * vez por sessão de uso. ``staleTime: 5min`` evita refetch enquanto o
 * partner navega entre páginas.
 */
export const usePortalSectors = () =>
  useQuery({
    queryKey: KEYS.sectors(),
    queryFn: () => portalAtendimentosApi.listSectors(),
    staleTime: 5 * 60 * 1000,
  });

/** Abre nova sessão; invalida lista pra refletir a sessão recém-criada. */
export const useStartPortalSession = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PortalStartSessionRequest) => portalAtendimentosApi.startSession(body),
    // Patch B5: cancela poll em voo da lista pra evitar resposta antiga
    // sobrescrever a invalidação que vem em onSuccess.
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: KEYS.list() });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list() });
    },
  });
};

/** Envia mensagem; invalida messages + list (last_message_at muda). */
export const useSendPortalMessage = (sessionId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PortalSendMessageBody) => portalAtendimentosApi.sendMessage(sessionId, body),
    // Patch B5 (ajustado Ciclo 2): cancela o POLL de mensagens em voo + a lista
    // antes de mutar — evita resposta stale mascarar a nova msg por 1 ciclo.
    // 🔑 `exact:true` no poll: SEM ele o match fuzzy do TanStack pegaria também a
    // query infinita BASE (`KEYS.messages`) e, se a carga inicial ainda estivesse
    // em voo, a cancelaria com revert → travaria em "Nenhuma mensagem" (staleTime
    // Infinity, sem retry). Só o poll (child key) deve ser cancelado.
    onMutate: async () => {
      await Promise.all([
        qc.cancelQueries({ queryKey: [...KEYS.messages(sessionId), "poll"], exact: true }),
        qc.cancelQueries({ queryKey: KEYS.list() }),
      ]);
    },
    onSuccess: () => {
      // Poll imediato (não invalida a query infinita das mensagens — refetcharia a
      // janela móvel e abriria gap com as páginas antigas). O `after_id` traz a
      // própria msg + concorrentes sem gap. (mesmo padrão do painel interno, Ciclo 1)
      qc.invalidateQueries({ queryKey: [...KEYS.messages(sessionId), "poll"] });
      qc.invalidateQueries({ queryKey: KEYS.list() });
    },
    // Patch E3: se backend rejeita ("session closed" 4xx), força
    // refetch da lista pra que `isClosed` flip imediatamente em vez
    // de aguardar 15s do poll.
    onError: () => {
      qc.invalidateQueries({ queryKey: KEYS.list() });
    },
  });
};

/**
 * Registra consent LGPD para gravação da sessão (PRD 4, Story 4.4.2).
 *
 * Mutation idempotente — chamada repetida retorna o mesmo `consent_at`
 * (vencedor da corrida preservado pelo UPDATE atômico). Invalida a lista
 * de sessões porque o consent muda o estado lógico da sessão; também
 * remove qualquer query de detail cacheada pra forçar refetch do flag
 * `consent_to_recording`/`consent_at` em telas que consumirem futuramente.
 */
export const useRecordConsent = (sessionId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => portalAtendimentosApi.recordConsent(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list() });
    },
  });
};

/**
 * Story A4 — status do consent de nota de voz do parceiro. A UI usa `granted`
 * pra decidir se mostra o `VoiceConsentModal` antes de gravar.
 */
export const useVoiceConsentStatus = () =>
  useQuery({
    queryKey: KEYS.voiceConsent(),
    queryFn: () => portalAtendimentosApi.getVoiceConsent(),
  });

/** Story A4 — concede o consent (libera o áudio). Invalida o status. */
export const useGrantVoiceConsent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => portalAtendimentosApi.grantVoiceConsent(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.voiceConsent() });
    },
  });
};

/** Story A4 — revoga o consent (bloqueia novas gravações). Invalida o status. */
export const useRevokeVoiceConsent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => portalAtendimentosApi.revokeVoiceConsent(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.voiceConsent() });
    },
  });
};
